import {
  segmentForEnd,
  formatSegmentDistance,
  formatSegmentFace,
  hasFaceChange,
} from "./segments";

/**
 * scoreSubtitle — per-stage header derivation for the live scoring page.
 *
 * Pure extraction of the stage chip + subtitle rules that used to sit inline in
 * Score.jsx (#2061). No React, no state: given a round and the end currently
 * being scored, return exactly what the context strip should print.
 */

/**
 * Per-stage distance + face for the current end (#1398 / M8.8 2a-4), plus the
 * subtitle that goes with them.
 *
 * Map the end being scored to its v2 segment so the header shows the archer's
 * NATIVE unit (a York stage shows "100 yd", never the converted 91 m) and the
 * per-stage face SIZE where a round changes face mid-round (WA1440 122→80cm).
 * A normal single-segment round resolves to one segment whose distance/face is
 * already shown via event_detail, so we only surface the per-stage face chip
 * when the round actually changes face (hasFaceChange) — no regression.
 *
 * A native distance chip is surfaced only when it adds information beyond the
 * existing event_detail line: a MULTI-stage round, where the distance varies
 * per stage and event_detail can't show all of them. A single-segment round
 * (imperial or metric) shows nothing extra here — event_detail already names
 * its one distance, so an extra chip only duplicates it (sandbox QA sweep
 * 2026-09-13, item 5: a yard round showed "20yd … 20 yd" in Edit mode).
 *
 * Subtitle (#1398 follow-up / M008.8 W4 #2): event_detail is server-stamped as
 * "{org} {face} · {distance} {unit}" — the round's NATIVE unit (#2345), so a
 * yard round's event_detail already ends "· 100 yd", not necessarily metres.
 * When we already show the NATIVE stage distance in the chip, that trailing
 * distance segment is redundant (and, before #2345, was flat wrong for a
 * yard round — "100 yd" stage but "· 91m" subtitle). Strip the trailing
 * distance segment in that case so the header stays internally consistent.
 * A plain metric round (no stage chip) keeps its subtitle verbatim.
 *
 * The stripped pattern accepts BOTH unit ("m" or "yd") and BOTH the current
 * spaced form ("18 m", "100 yd") and the pre-spacing-fix unspaced form
 * ("18m", "100yd") a round created before that fix may still carry in its
 * stored `event_detail` snapshot (event_detail spacing fix, 2026-09-14) —
 * the space between the number and the unit is optional.
 */
export function scoreStageDisplay(round, currentEndNumber) {
  if (!round) {
    return {
      currentSegment: null,
      stageFace: null,
      stageDistance: null,
      eventDetailDisplay: null,
    };
  }
  const currentSegment = segmentForEnd(round.segments, currentEndNumber);
  const roundChangesFace = hasFaceChange(round.segments);
  const stageFace = roundChangesFace ? formatSegmentFace(currentSegment) : null;
  const multiStage = (round.segments?.length ?? 0) > 1;
  // #<QA-5, sandbox sweep 2026-09-13> — a single-segment round's stage chip
  // duplicated the distance already shown in event_detail (e.g. "20yd …
  // 20 yd"). Only a MULTI-stage round needs the per-stage chip at all — that's
  // the only case where the distance can differ from what event_detail shows
  // and the chip disambiguates something real. A single segment, imperial or
  // not, adds nothing.
  const stageDistance = multiStage ? formatSegmentDistance(currentSegment) : null;
  const eventDetailDisplay =
    stageDistance && round.event_detail
      ? round.event_detail.replace(
          /\s*[·•]\s*\d+(?:\.\d+)?\s*(?:m|yd)\b\s*$/i,
          "",
        )
      : round.event_detail;
  return { currentSegment, stageFace, stageDistance, eventDetailDisplay };
}

/**
 * Break-boundary cue payload (#1399) for a boundary already resolved by
 * `breakBoundaryInfo`. `subtotal` is the round total BEFORE this end: the newly
 * entered break has no confirmed ends yet, so round.total is the sum of all
 * PREVIOUS breaks at this exact moment.
 */
export function breakCuePayload(round, info) {
  return {
    breakIndex: info.breakIndex,
    subtotal: round.total,
    distanceLabel: info.distanceChanged
      ? formatSegmentDistance(info.segment)
      : null,
    faceLabel: info.faceChanged ? formatSegmentFace(info.segment) : null,
    breakLabel:
      info.segment?.label ||
      formatSegmentDistance(info.segment) ||
      `Break ${info.breakIndex + 1}`,
  };
}
